import { useTerminalStore } from '@/stores/terminalStore';
import { useProjectActivityStore } from '@/stores/projectActivityStore';

/**
 * A small status dot overlaid on a project's icon in the project lists.
 *
 *  - **hidden**  — no terminal open for the project and no recent activity.
 *  - **idle**    — a terminal is open (even if not focused): steady dark-gray dot,
 *                  visible against the dark theme.
 *  - **active**  — recent terminal output (within ~3s, via projectActivityStore):
 *                  the dot turns green and pulses to draw attention.
 *
 * Absolutely positioned — the caller wraps the icon in a `relative` element.
 */
export default function ProjectActivityDot({ projectPath }: { projectPath: string }) {
  // Reactive: does this project have at least one open (active) terminal?
  const hasTerminal = useTerminalStore((s) => {
    for (const sess of s.sessions.values()) {
      if (sess.projectPath === projectPath && sess.status === 'active') return true;
    }
    return false;
  });

  // Reactive: recent terminal output (drives the pulse). projectActivityStore
  // sets a 3s expiry per project on each output burst.
  const active = useProjectActivityStore((s) => {
    const expiresAt = s.activities.get(projectPath);
    return !!expiresAt && Date.now() < expiresAt;
  });

  if (!hasTerminal && !active) return null;

  return (
    <span
      aria-hidden
      title={active ? 'Active — terminal activity' : 'Terminal open'}
      className={`pointer-events-none absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full ring-2 ring-base-100 ${
        active
          ? 'bg-emerald-400 animate-pulse shadow-[0_0_6px_2px_rgba(52,211,153,0.6)]'
          : 'bg-stone-500'
      }`}
    />
  );
}
